
import { Player } from "@/types/tournament";

// Shuffle an array using Fisher-Yates algorithm
export function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

// Assign players to courts with bench rotation
export function assignPlayersToCourts(players: Player[], courtsCount: number = 3): Player[][] {
  const totalCourtPlayers = courtsCount * 4;
  
  if (players.length < totalCourtPlayers) {
    throw new Error(`Need at least ${totalCourtPlayers} players for ${courtsCount} courts`);
  }

  // Reset all players bench status
  const allPlayers = players.map(p => ({ ...p, isOnBench: false }));
  
  // If we have more than 12 players, some need to be on bench
  if (allPlayers.length > totalCourtPlayers) {
    // Find players who have played the least matches
    const sortedByMatches = [...allPlayers].sort((a, b) => {
      if (a.matchesPlayed !== b.matchesPlayed) {
        return a.matchesPlayed - b.matchesPlayed; // Less matches played first
      }
      return Math.random() - 0.5; // Random if same matches played
    });
    
    // Select players for courts (those with fewer matches played)
    const playingPlayers = sortedByMatches.slice(0, totalCourtPlayers);
    const benchPlayers = sortedByMatches.slice(totalCourtPlayers);
    
    // Mark bench players
    benchPlayers.forEach(player => {
      const originalPlayer = allPlayers.find(p => p.id === player.id);
      if (originalPlayer) {
        originalPlayer.isOnBench = true;
      }
    });
    
    // Update the main players array
    allPlayers.forEach(player => {
      const benchPlayer = benchPlayers.find(p => p.id === player.id);
      if (benchPlayer) {
        player.isOnBench = true;
      }
    });
    
    // Shuffle playing players randomly
    const shuffledPlayers = shuffleArray(playingPlayers);
    const courts: Player[][] = [];

    // Divide players into courts
    for (let i = 0; i < courtsCount; i++) {
      courts.push(shuffledPlayers.slice(i * 4, (i + 1) * 4));
    }

    return courts;
  }

  // Standard case: exactly 12 players
  const shuffledPlayers = shuffleArray(allPlayers);
  const courts: Player[][] = [];

  // Divide players into courts
  for (let i = 0; i < courtsCount; i++) {
    courts.push(shuffledPlayers.slice(i * 4, (i + 1) * 4));
  }

  return courts;
}

// Get team assignments for each court (each court has 2 teams of 2 players)
export function getTeamsInCourt(court: Player[]): [Player[], Player[]] {
  if (court.length !== 4) {
    throw new Error("Each court must have exactly 4 players");
  }
  
  // Team 1: players 0 and 1, Team 2: players 2 and 3
  return [[court[0], court[1]], [court[2], court[3]]];
}

// Update standings after a round - for padel, we only track actual match performance
export function updateStandings(
  players: Player[],
  courtResults: { court: number; winner: "team1" | "team2"; score: [number, number] }[]
): Player[] {
  const updatedPlayers = [...players];
  
  courtResults.forEach(result => {
    const courtIndex = result.court;
    const courtPlayers = updatedPlayers.slice(courtIndex * 4, (courtIndex + 1) * 4);
    
    // In padel, we don't need artificial tournament points
    // The actual match scores (stored in roundScores) are used for ranking
    
    // Update matches played for playing players only
    courtPlayers.forEach(player => {
      player.matchesPlayed += 1;
    });
  });
  
  return updatedPlayers;
}

// Reorganize courts for the next round based on actual match performance
export function reorganizeCourts(
  courts: Player[][],
  results: { court: number; winner: "team1" | "team2"; score: [number, number] }[]
): Player[][] {
  const allPlayers = courts.flat();
  
  // Sort all players by total match points scored (roundScores), not artificial tournament points
  const sortedPlayers = [...allPlayers].sort((a, b) => {
    const aTotalMatchPoints = (a.roundScores || []).reduce((sum, score) => sum + score, 0);
    const bTotalMatchPoints = (b.roundScores || []).reduce((sum, score) => sum + score, 0);
    
    // Primary sort: total match points scored
    if (bTotalMatchPoints !== aTotalMatchPoints) {
      return bTotalMatchPoints - aTotalMatchPoints;
    }
    
    // Tiebreaker: matches played
    return b.matchesPlayed - a.matchesPlayed;
  });
  
  // Create new courts based on ranking
  const newCourts: Player[][] = [];
  
  // Distribute players to courts based on ranking
  // Court 1 gets highest ranked players, Court 2 gets mid-ranked, Court 3 gets lowest ranked
  const courtsCount = courts.length;
  for (let i = 0; i < courtsCount; i++) {
    const courtPlayers: Player[] = [];
    for (let j = 0; j < 4; j++) {
      const playerIndex = i * 4 + j;
      if (playerIndex < sortedPlayers.length) {
        courtPlayers.push(sortedPlayers[playerIndex]);
      }
    }
    newCourts.push(courtPlayers);
  }

  // Now we need to randomize the teams within each court
  return newCourts.map(court => shuffleArray(court));
}

// Score comparison function for leaderboard
export function comparePlayerScores(a: Player, b: Player): number {
  // Sort by points (descending)
  if (b.points !== a.points) {
    return b.points - a.points;
  }
  
  // If points are equal, sort by name
  return a.name.localeCompare(b.name);
}
